import type { FC } from "react";

import type { DashboardAdminUser } from "../../dashboard-client";

import { formatRelativeTime } from "../../utils/time";
import { Badge } from "../common/Badge";
import { Button } from "../common/Button";
import { DataTable } from "../common/DataTable";
import { StatusBadge } from "../common/StatusBadge";

export const AdminUserRow: FC<{
  user: DashboardAdminUser;
  onInvalidate: (userId: string) => void;
}> = ({ user, onInvalidate }) => (
  <DataTable.Row>
    <DataTable.Cell className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
      <div className="space-y-0.5">
        <div>{user.email}</div>
        <div className="text-[10px] font-normal text-slate-500 sm:hidden dark:text-slate-400">
          {user.name ?? <StatusBadge status={user.global_role} size="sm" />}
        </div>
      </div>
    </DataTable.Cell>
    <DataTable.Cell className="hidden text-sm text-gray-600 sm:table-cell dark:text-gray-300">
      {user.name ?? <span className="text-gray-400 dark:text-gray-500">—</span>}
    </DataTable.Cell>
    <DataTable.Cell className="hidden md:table-cell">
      <StatusBadge status={user.global_role} size="sm" />
    </DataTable.Cell>
    <DataTable.Cell className="hidden lg:table-cell">
      {user.organizations.length === 0 ? (
        <span className="text-xs text-gray-400 dark:text-gray-500">None</span>
      ) : (
        <div className="flex flex-wrap gap-1">
          {user.organizations.map((org) => (
            <Badge key={org.organization_id} variant="default" size="sm">
              {org.name}
              <span className="ml-1 opacity-60">({org.role})</span>
            </Badge>
          ))}
        </div>
      )}
    </DataTable.Cell>
    <DataTable.Cell className="hidden text-xs text-gray-700 md:table-cell dark:text-gray-300">
      {user.active_sessions > 0 ? (
        <Badge variant="success" size="sm">
          {user.active_sessions}
        </Badge>
      ) : (
        <span className="text-gray-400 dark:text-gray-500">0</span>
      )}
    </DataTable.Cell>
    <DataTable.Cell className="hidden whitespace-nowrap text-xs text-gray-500 xl:table-cell dark:text-gray-400">
      {formatRelativeTime(new Date(user.created_at).getTime())}
    </DataTable.Cell>
    <DataTable.Cell>
      {user.active_sessions > 0 ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onInvalidate(user.user_id)}
        >
          Invalidate Sessions
        </Button>
      ) : (
        <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
      )}
    </DataTable.Cell>
  </DataTable.Row>
);
